/* Login component */
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { APP_URL, MICROSOFT_CLIENT_ID, GOOGLE_CLIENT_ID } from "settings";
import { useGetCall, usePost } from "seed/api";
import { Loading } from "seed/helpers";
import { detectBrowserLanguage, getUserLanguage } from "components/util/l.lib";
import l from "components/util/l";
import View from "./Login.view";


function Login(props) {

  const params = new URLSearchParams(props.location.search);
  const [flow, setFlow] = useState(null)
  const [error, setError] = useState(null);
  const [rememberMe, setRememberMe] = useState(true);


  const [callGetFlow, reqGetFlow] = usePost("/flows/get_public_data", {
    onCompleted: (data) => {
      setFlow(data);
      // A token link that goes through login pins the session to its flow (see Presentation.js)
      localStorage.setItem("flow_token", data.token);
    },
    // A segment of `next` that is an app route rather than a token answers 404: plain login
    captureErrors: false
  })

  const [callSetLanguage] = usePost("/users/set_language", {}, {});

  const login = (data) => {
    if (rememberMe) {
      setRememberMe(true);
      localStorage.setItem("token", data.key);
      localStorage.setItem("id", data.user);
    }
    sessionStorage.setItem("token", data.key);
    sessionStorage.setItem("id", data.user);
    
    // Check flow access if applicable
    if (flow) callHasFlowAccess({ token: flow.token })
    else callAuth({ user_id: data.user });
  };

  const completeLogin = async (data) => {
    // Check if user is verified and not anonymous
    if (!data.token_verified || data.is_anonymous) {
      setError(
        data.is_anonymous
          ? l("Usuario o contraseña incorrectos%VB008")
          : l("Su cuenta no ha sido verficada, por favor revise su correo para verificarla.%VB009")
      );
      sessionStorage.clear();
      return;
    }

    // Set language preference
    if (data.account_settings?.language)
      localStorage.setItem("language", data.account_settings.language);
    else {
      // If there is no language set, detect and store browser language
      const browserLang = detectBrowserLanguage();
      localStorage.setItem("language", browserLang);
      await callSetLanguage({ language: browserLang });
    }

    // Handle next page navigation
    const next = localStorage.getItem("next");
    localStorage.removeItem("next");
    // Navigate to next page after login if present, else to flow or app home
    window.location.replace(params.get("next") ?? next ?? (flow ? `/app/${flow.token}` : "/app"));
  }

  const [callAuth] = useGetCall("/auth/user", "", {
    onCompleted: async (data) => completeLogin(data),
    onError: () => {
      sessionStorage.clear();
      setError(l("Usuario o contraseña incorrectos%VB008"));
    },
    captureErrors: false
  });

  const [callHasFlowAccess, reqHasFlowAccess] = usePost("/flows/has_access", {
    onCompleted: () => callAuth({ user_id: sessionStorage.getItem("id") }),
    onError: () => setError(l("No cuentas con permisos para esta solución, consulta al administrador%VB049")),
    captureErrors: false
  })

  const [callLogin, reqLogin] = usePost("/auth/login", {
    onCompleted: (data) => login(data),
    onError: () => setError(l("Usuario o contraseña incorrectos%VB008")),
    includeAuth: false,
    captureErrors: false
  });

  const [callSSO, reqSSO] = usePost("/users/login_sso", {
    onCompleted: (data) => login(data),
    onError: () => setError(l("Cuenta o usuario no registrado%VB010")),
    includeAuth: false,
    captureErrors: false
  });


  // On component mount
  useEffect(() => {
    handleSSOCallback()
    initFlowData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const handleSSOCallback = () => {
    const code = params.get("code");
    const next = params.get("next");
    if (!code) return
    if (next) localStorage.setItem("next", next);
    callSSO({
      code: code,
      app: code.startsWith("M.") ? "Microsoft" : "Google",
      language: getUserLanguage()
    });
  }

  const initFlowData = () => {
    // A first visit by a link like /app/<token> has nothing stored yet: the token is in `next`
    const nextPath = params.get("next") ?? "";
    const routeToken = nextPath.startsWith("/app/") && nextPath.split("/")[2];
    const flowToken = localStorage.getItem("flow_token") || routeToken
    if (flowToken) callGetFlow({ token: flowToken })
  }

  const onSubmit = (values) => {
    const { email, password, rememberMe } = values;
    setRememberMe(rememberMe);
    callLogin({ email: email, password: password });
  };

  const onClickMicrosoftBtn = () =>
    // eslint-disable-next-line max-len
    (window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${MICROSOFT_CLIENT_ID}&response_type=code&redirect_uri=${APP_URL}/login&response_mode=query&scope=openid%20profile%20email%20User.Read%20offline_access`);

  const onClickGoogleBtn = () =>
    // eslint-disable-next-line max-len
    (window.location.href = `https://accounts.google.com/o/oauth2/auth?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${APP_URL}/login&scope=openid%20profile%20email`);


  if (reqGetFlow.loading) return <Loading />

  const isLoading = reqLogin.loading || reqSSO.loading || reqHasFlowAccess.loading;

  return (
    <View
      flow={flow}
      error={error}
      isLoading={isLoading}
      onSubmit={onSubmit}
      onClickMicrosoftBtn={onClickMicrosoftBtn}
      onClickGoogleBtn={onClickGoogleBtn}
    />
  );
}

export default Login;
